import React from 'react';
import './App.scss';

import { SpaRenderWithBrowserRouter } from '@echino/echino.ui.framework'
import { SpaRenderStatus } from '@echino/echino.ui.framework/components/SpaBuilder/common/ISpaRenderProps';

// Global variables coming from the server
// 'declare' them here to avoid typescript compilation errors

// The auml code of the app to display
declare var aumlContent: string;

// Name of the scenario project this app belongs to
declare var serviceName: string;

// A dicitonary containing the default version of each package
declare var packageVersions: { [name: string]: string };

// Information on the tenant
declare var tenant: any;

// Information on the authenticated user
declare var user: any;

// The languages available for this app
declare var languages: string[];

declare var appManifest: string;

interface IAppProps {

}

interface IAppState {
    loaded: boolean;
    inspectorEnabled: boolean;
}

export default class App extends React.Component<IAppProps, IAppState> {

    _refreshTokenTimer: NodeJS.Timeout | undefined;

    constructor(props: IAppProps) {
        super(props);

        this.state = {
            loaded: false,
            inspectorEnabled: false
        }

        // Add a function that we can call from the browser console, allowing us to debug the page
        //TODO we may not want to do that for prod pages
        //@ts-ignore
        window.toggleInspector = () => {
            this.setState({
                inspectorEnabled: !this.state.inspectorEnabled
            },
                () => console.log(`Auml inspector is now ${this.state.inspectorEnabled ? 'enabled' : 'disabled'}`)
            )

        } 
    }

    componentDidMount() {

        try {
            // Setup a method to refresh our access token when it expires
            if (this._refreshTokenTimer) {
                clearTimeout(this._refreshTokenTimer);
            }

            if (!user) {
                console.log("No user found, will not refresh the token");
                return;
            }

            let timeToExpireMs = this.getTimeToTokenExpiration();

            if (timeToExpireMs === null) {
                console.log("No identity token found, attempting refreshing the token in 5 minutes");
                this._refreshTokenTimer = setTimeout(() => this.refreshExpiredToken(), 5 * 60 * 1000);
                return;
            }

            if (timeToExpireMs <= 0) {

                // Refresh it immediatelty
                this.refreshExpiredToken();
            }
            else {
                this._refreshTokenTimer = setTimeout(() => this.refreshExpiredToken(), timeToExpireMs);
            }
        }
        catch (err) {
            console.warn("Could not setup token refresh timer: " + err)
        }
    }

    componentWillUnmount() {

        // Cleanup the refresh of the token
        if (this._refreshTokenTimer) {
            clearTimeout(this._refreshTokenTimer);
        }
    }

    progress(s: SpaRenderStatus) {
        //console.log('progessStatus ', s);
        if (s === 'completed') {
            this.setState({ loaded: true })
        }
    }

    getLogo() {
        //@ts-ignore
        let tenant: any = global.tenant;

        if (tenant.logoUrl) {
            return <img src={tenant.logoUrl} alt={tenant.displayName} />
        }
        else {
            return <div className='app-loading-title'>{tenant.displayName}</div>
        }
    }

    getMode(theme: boolean | undefined) {

        if (theme !== undefined) {
            return theme ? 'dark' : 'light';
        }

        if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
            return 'dark'
        }

        return 'light'
    }

    // Get input from the query parameters in the url
    retrieveInputs() {

        return Object.fromEntries(new URLSearchParams(window.location.search).entries());

    }

    getTimeToTokenExpiration(): number | null {

        // The identity token is the only one we have access here on the client
        // We use it to know when the access token (which we can't read) will expire
        let identityToken: string | null = null;

        let identityTokenKey = `${tenant.name}IdToken=`;
        let cookies = document.cookie.split(';');
        for (let c of cookies) {

            if (c.trim().startsWith(identityTokenKey))
            {
                identityToken = c.trim().slice(identityTokenKey.length);
                break;
            }
        }

        if (identityToken === null) {
            return null; // token not found
        }

        let identityTokenParsed = this.parseJwt(identityToken);

        let expires = identityTokenParsed.exp; // Timestamp in second since Unix epoch
        let timeToExpiresMs = expires * 1000 - new Date().getTime();

        let timeToExpireStr = this.msToTime(timeToExpiresMs);

        console.log(`Token will expire at ${new Date(expires * 1000).toISOString()} (in ${timeToExpireStr}), setting up a timer to refresh it`);

        // Refresh it a bit before the expiration (5 min)
        timeToExpiresMs -= 5 * 60 * 1000;

        return timeToExpiresMs;
    }


    getTimeToNewTokenExpiration(expirationDateTime: string): number {

        let timeToExpiresMs = new Date(expirationDateTime).getTime() - new Date().getTime();

        // Refresh it a bit before the expiration (5 min)
        timeToExpiresMs -= 5 * 60 * 1000;

        return timeToExpiresMs;
    }

    msToTime(ms: number) {

        let seconds = Math.floor((ms / 1000) % 60),
            minutes = Math.floor((ms / (1000 * 60)) % 60),
            hours = Math.floor((ms / (1000 * 60 * 60)) % 24);

        let timeString = seconds + " seconds";

        if (minutes > 0) {
            timeString = minutes + " minutes " + timeString;
        }

        if (hours > 0) {
            timeString = hours + " hours " + timeString;
        }

        return timeString;
    }

    parseJwt(token: string) {
        var base64Url = token.split('.')[1];
        var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        var jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));

        return JSON.parse(jsonPayload);
    }

    render() {

        //var clusterUrl = this.figureOutClusterUrl();

        let inputs = this.retrieveInputs();

        let theme = undefined;
        if (inputs["_theme"]) {
            theme = inputs["_theme"].toLowerCase() === "dark";
        }

        let aumlManifest = null;

        try {
            let appManifestObj = JSON.parse(appManifest); 
            aumlManifest = appManifestObj.auml
        }
        catch { }

        return (
            <div className="app">
                <SpaRenderWithBrowserRouter
                    appDescription={aumlContent}
                    tenant={tenant}
                    serviceName={serviceName}
                    packageVersions={packageVersions}
                    user={user}
                    languages={languages}
                    onProgress={(s) => this.progress(s)}
                    input={inputs}
                    manifest={aumlManifest}
                    theme={theme}
                    inspectorEnabled={this.state.inspectorEnabled}
                />

                {!this.state.loaded &&
                    <div className={`app-loading ${this.getMode(theme)}`}>
                        {this.getLogo()}

                        <i className="fas fa-circle-notch fa-spin"></i>
                    </div>
                }
            </div>
        );
    }

    async refreshExpiredToken() {

        console.log("Token will expire soon, requesting a new one...");

        // Using the refresh token we ask for a new access token using /portal/login/refresh
        // If the refresh token has also expired, we will be redirected to the login page
        //TODO the redirection in case of refresh failure doesn't seem to work and if the page is public this is probably not the right move

        let redirectUrl = window.location.href; // Where to send us back in case we need to be redirected to the login page
        let url = `${tenant.clusterUrl}/portal/login/refresh?redirectUrl=${encodeURIComponent(redirectUrl)}`;

        let response = await fetch(url); // For this request to work, we need to have a refresh token in the cookies

        if (response.ok) {

            // Setup next refresh
            let timeToExpireMs;
            try {
                let newTokenExpiration = await response.text();
                timeToExpireMs = this.getTimeToNewTokenExpiration(newTokenExpiration);
            }
            catch
            {
                timeToExpireMs = 2 * 60 * 60 * 1000; // refresh in 2 hours
            }
            
            console.log(`Refresh request ok, next refresh in ${this.msToTime(timeToExpireMs)}`);

            this._refreshTokenTimer = setTimeout(() => this.refreshExpiredToken(), timeToExpireMs);
        }
        else {
            let content = await response.text();

            console.error("Could not refresh the token", content);
            console.log("Retrying refreshing the token in 5 minutes...");
            this._refreshTokenTimer = setTimeout(() => this.refreshExpiredToken(), 5 * 60 * 1000);
        }
    }


    /*
    figureOutClusterUrl(): string {

        //TODO this only work if going through SF reverse proxy
        var origin = window.location.origin;
        if (typeof fabricAppName !== 'undefined') // Check if the variable has been declared
        {
            return `${origin}/${fabricAppName}`;
        }

        return origin;
    }
    */
}

